import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getAllDepartments,
    getEmployeeContains,
} from '@/modules/Employee/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Typogaphy } from 'vnetwork-ui'
import { AssetFormData } from '.'
import { VersionBumpType } from '@/modules/Asset/utils/version-bump'
import { formatDateWithFormatter } from '@/utils/function'
import ReviewVersionChangesCard from './sections/ReviewVersionChangesCard'
import ReviewFilesCard from './sections/ReviewFilesCard'
import ReviewSharingCard from './sections/ReviewSharingCard'

interface Step3ReviewProps {
    formData: AssetFormData
    isUpdateMode?: boolean
    versionBumpType?: VersionBumpType
    currentVersion?: { major: number; minor: number; patch: number }
    nextVersion?: { major: number; minor: number; patch: number }
    originalFormData?: Partial<AssetFormData> | null
    originalFileCount?: number
}

/** Human-readable labels for form field keys */
const FIELD_LABELS: Record<string, string> = {
    assetName: 'form.name',
    assetDescription: 'labels.assetDescription',
    documentGroup: 'labels.documentGroup',
    documentType: 'labels.documentType',
    departmentId: 'form.department',
    confidentialityLevel: 'labels.confidentiality',
    integrityLevel: 'labels.integrity',
    availabilityLevel: 'labels.availability',
    startDate: 'labels.startDate',
    endDate: 'labels.endDate',
    shareScope: 'form.shareScope',
    isReadConfirmationRequired: 'form.readConfirmationRequired',
}

/** Translation prefix map for enum fields */
const ENUM_PREFIX: Partial<Record<keyof AssetFormData, string>> = {
    documentGroup: 'document_groups',
    documentType: 'document_types',
    confidentialityLevel: 'confidentiality_levels',
    integrityLevel: 'integrity_levels',
    availabilityLevel: 'availability_levels',
}

const Step3Review: React.FC<Step3ReviewProps> = ({
    formData,
    isUpdateMode,
    versionBumpType,
    currentVersion,
    nextVersion,
    originalFormData,
    originalFileCount = 0,
}) => {
    const { t } = useTranslation('asset')

    const { data: departmentsData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DEPARTMENTS],
        queryFn: getAllDepartments,
    })

    const { data: employeesData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.EMPLOYEES,
            'contains',
            formData.shared_employee_ids,
        ],
        queryFn: () =>
            getEmployeeContains({
                employee_ids: formData.shared_employee_ids,
            }),
        enabled: formData.shared_employee_ids.length > 0,
    })

    const { data: notifyEmployeesData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.EMPLOYEES,
            'notification-contains',
            formData.notify_employee_ids,
        ],
        queryFn: () =>
            getEmployeeContains({
                employee_ids: formData.notify_employee_ids,
            }),
        enabled:
            formData.notifyScope === 'custom' &&
            formData.notify_employee_ids.length > 0,
    })

    const getDepartmentName = (id: string | number) => {
        if (!departmentsData?.results) return t('form.loading')
        const dept = departmentsData.results.find(
            (d) => d.department_id.toString() === id.toString()
        )
        return dept?.department_name || t('form.notSelected')
    }

    const changedFields = useMemo(() => {
        if (!isUpdateMode || !originalFormData) return []
        const changes: {
            field: string
            label: string
            oldValue: string
            newValue: string
        }[] = []

        const stringify = (key: keyof AssetFormData, v: unknown): string => {
            if (v === null || v === undefined || v === '') return '-'
            if (Array.isArray(v)) return v.length > 0 ? v.join(', ') : '-'
            if (typeof v === 'boolean') return v ? 'Yes' : 'No'
            const prefix = ENUM_PREFIX[key]
            if (prefix) return t(`${prefix}.${v}`)
            if (key === 'departmentId') return getDepartmentName(String(v))
            return String(v)
        }

        for (const key of Object.keys(
            FIELD_LABELS
        ) as (keyof AssetFormData)[]) {
            if (!(key in originalFormData)) continue
            const oldVal = originalFormData[key]
            const newVal = formData[key]
            if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                changes.push({
                    field: key,
                    label: FIELD_LABELS[key] || key,
                    oldValue: stringify(key, oldVal),
                    newValue: stringify(key, newVal),
                })
            }
        }

        const currentFileCount = formData.uploadedFiles?.length || 0
        if (currentFileCount !== originalFileCount) {
            changes.push({
                field: 'files',
                label: 'form.uploadedFiles',
                oldValue: `${originalFileCount}`,
                newValue: `${currentFileCount}`,
            })
        }

        return changes
    }, [
        isUpdateMode,
        originalFormData,
        formData,
        originalFileCount,
        departmentsData,
    ])

    return (
        <div className="space-y-2">
            {/* Version Update & Changes Summary (update mode only) */}
            {isUpdateMode &&
                currentVersion &&
                nextVersion &&
                versionBumpType && (
                    <ReviewVersionChangesCard
                        versionBumpType={versionBumpType}
                        currentVersion={currentVersion}
                        nextVersion={nextVersion}
                        changedFields={changedFields}
                    />
                )}

            {/* General Information */}
            <Card
                className="border-gray-100 shadow-sm overflow-hidden"
                data-tour="review-info"
            >
                <CardHeader className="border-b border-gray-50 pb-3">
                    <CardTitle className="flex items-center gap-2">
                        <Typogaphy className="font-semibold text-primary">
                            {t('form.assetSummary')}
                        </Typogaphy>
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        <ReviewField
                            label={t('form.name')}
                            value={formData.assetName || t('form.notProvided')}
                        />
                        <ReviewField
                            label={t('labels.documentGroup')}
                            value={
                                formData.documentGroup
                                    ? t(
                                          `document_groups.${formData.documentGroup}`
                                      )
                                    : t('form.notSelected')
                            }
                        />
                        <ReviewField
                            label={t('labels.documentType')}
                            value={
                                formData.documentType
                                    ? t(
                                          `document_types.${formData.documentType}`
                                      )
                                    : t('form.notSelected')
                            }
                        />
                        <ReviewField
                            label={t('form.department')}
                            value={getDepartmentName(formData.departmentId)}
                        />
                        <ReviewField
                            label={t('labels.startDate')}
                            value={
                                formData.startDate
                                    ? formatDateWithFormatter(
                                          formData.startDate,
                                          'DD/MM/YYYY'
                                      )
                                    : t('form.notProvided')
                            }
                        />
                        <ReviewField
                            label={t('labels.endDate')}
                            value={
                                formData.endDate
                                    ? formatDateWithFormatter(
                                          formData.endDate,
                                          'DD/MM/YYYY'
                                      )
                                    : t('form.noEndDate')
                            }
                        />
                        {formData.documentType === 'REGULATION' &&
                            formData.isReadConfirmationRequired && (
                                <ReviewField
                                    label={t('form.readConfirmationRequired')}
                                    value={t('common.yes')}
                                />
                            )}
                    </div>
                </CardContent>
            </Card>

            {/* Security Classification */}
            <Card className="border-gray-100 shadow-sm overflow-hidden">
                <CardHeader className="border-b border-gray-50 pb-3">
                    <CardTitle className="flex items-center gap-2">
                        <Typogaphy className="font-semibold text-primary">
                            {t('form.securityClassification')}
                        </Typogaphy>
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-5">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <SecurityField
                            label={t('labels.confidentiality')}
                            value={
                                formData.confidentialityLevel
                                    ? t(
                                          `confidentiality_levels.${formData.confidentialityLevel}`
                                      )
                                    : t('form.notSelected')
                            }
                        />
                        <SecurityField
                            label={t('labels.integrity')}
                            value={
                                formData.integrityLevel
                                    ? t(
                                          `integrity_levels.${formData.integrityLevel}`
                                      )
                                    : t('form.notSelected')
                            }
                        />
                        <SecurityField
                            label={t('labels.availability')}
                            value={
                                formData.availabilityLevel
                                    ? t(
                                          `availability_levels.${formData.availabilityLevel}`
                                      )
                                    : t('form.notSelected')
                            }
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Sharing Settings */}
            <ReviewSharingCard
                shareScope={formData.shareScope}
                employees={employeesData || []}
                departmentIds={formData.shared_department_ids}
                getDepartmentName={getDepartmentName}
            />

            {/* Description */}
            {formData.assetDescription && (
                <Card className="border-gray-100 shadow-sm overflow-hidden">
                    <CardHeader className="border-b border-gray-50 pb-3">
                        <CardTitle className="flex items-center gap-2">
                            <Typogaphy className="font-semibold text-primary">
                                {t('labels.assetDescription')}
                            </Typogaphy>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-5">
                        <p className="text-sm text-gray-600 leading-relaxed italic">
                            &ldquo;{formData.assetDescription}&rdquo;
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Uploaded Files */}
            <ReviewFilesCard files={formData.uploadedFiles || []} />

            {/* Notification Recipients */}
            {formData.notifyScope && formData.notifyScope !== 'none' && (
                <Card className="border-gray-100 shadow-sm overflow-hidden">
                    <CardHeader className="border-b border-gray-50 pb-3">
                        <CardTitle className="flex items-center gap-2">
                            <Typogaphy className="font-semibold text-primary">
                                {t('labels.notificationRecipients')}
                            </Typogaphy>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-5 text-sm text-gray-900">
                        {formData.notifyScope === 'all' ? (
                            <span>{t('form.notifyAll')}</span>
                        ) : (
                            <div className="space-y-3">
                                <ReviewField
                                    label={t('form.notifyDepartments')}
                                    value={
                                        formData.notify_department_ids.length >
                                        0
                                            ? formData.notify_department_ids
                                                  .map(getDepartmentName)
                                                  .join(', ')
                                            : t('form.notSelected')
                                    }
                                />
                                <ReviewField
                                    label={t('form.notifyEmployeesReview')}
                                    value={
                                        notifyEmployeesData?.length
                                            ? notifyEmployeesData
                                                  .map(
                                                      (employee) =>
                                                          employee.label
                                                  )
                                                  .join(', ')
                                            : t('form.notSelected')
                                    }
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Additional Notes */}
            {formData.notes && (
                <Card className="border-gray-100 shadow-sm overflow-hidden">
                    <CardHeader className="border-b border-gray-50 pb-3">
                        <CardTitle className="flex items-center gap-2">
                            <Typogaphy className="font-semibold text-primary">
                                {t('form.changeNote')}
                            </Typogaphy>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-5">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                            {formData.notes}
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    )
}

/** Reusable row for key-value review fields */
const ReviewField: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <div className="flex justify-between items-center py-1">
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
)

/** Reusable block for security classification fields */
const SecurityField: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <div className="p-1">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">
            {label}
        </p>
        <p className="text-sm font-semibold text-gray-900">{value}</p>
    </div>
)

export default Step3Review
